/**
 * Shared dashboard widgets used by both Financial and Support dashboards.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import useSmartPoll from "@/lib/useSmartPoll";
import {
  Users, UserPlus, UserMinus, XCircle, CheckCircle, Plug,
  ArrowsLeftRight, Warning,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, Legend,
} from "recharts";

// ---------- Count-up hook ----------
export function useCountUp(target = 0, duration = 600) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target === null || target === undefined) return;
    let raf, start;
    const to = Number(target) || 0;
    const tick = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

// ---------- Mini Stat ----------
export function MiniStat({ label, value, tone = "slate", icon: Icon, hint, testid }) {
  // Single-color minimalist: numbers stay slate-900 universally; tone only colors a small
  // 2px top accent line so meaning stays without "candy" effect.
  const accent = {
    slate:   "bg-slate-300",
    emerald: "bg-emerald-500",
    indigo:  "bg-indigo-500",
    amber:   "bg-amber-500",
    red:     "bg-red-500",
    blue:    "bg-blue-500",
  }[tone] || "bg-slate-300";
  const animated = useCountUp(value);
  return (
    <div className="bg-white border border-slate-200 p-4 relative" data-testid={testid}>
      <div className={`absolute top-0 left-0 right-0 h-[2px] ${accent}`} />
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
        {Icon && <Icon size={13} weight="regular" className="text-slate-400" />}
      </div>
      <div className="mt-2 text-2xl font-semibold font-mono tabular-nums text-slate-900">{animated}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 truncate">{hint}</div>}
    </div>
  );
}

// ---------- ClientSummary widget ----------
export function ClientSummary() {
  const [k, setK] = useState(null);
  useSmartPoll(() => {
    api.get("/dashboard/client-summary").then(r => setK(r.data)).catch(() => {});
  }, 60000);
  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="flex items-baseline justify-between mb-4 pb-3 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Resumen de clientes</div>
          <div className="text-sm text-slate-700 mt-0.5">Estado actual del portafolio</div>
        </div>
        <div className="text-2xl font-semibold font-mono tabular-nums text-slate-900">{k?.total ?? 0}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat testid="cs-active" label="Activos" tone="emerald" icon={CheckCircle} value={k?.active} />
        <MiniStat testid="cs-pending" label="Pendientes" tone="amber" icon={Plug} value={k?.pending} />
        <MiniStat testid="cs-cancelled" label="Cancelados" tone="red" icon={XCircle} value={k?.cancelled} />
        <MiniStat testid="cs-disconnected" label="Desconectados" tone="red" icon={Warning} value={k?.disconnected} />
        <MiniStat testid="cs-transferred" label="Trasladados" tone="slate" icon={ArrowsLeftRight} value={k?.transferred} />
        <MiniStat testid="cs-new-month" label="Nuevos del mes" tone="indigo" icon={UserPlus} value={k?.new_this_month} />
        <MiniStat testid="cs-lost-month" label="Perdidos del mes" tone="slate" icon={UserMinus} value={k?.lost_this_month} />
        <MiniStat testid="cs-cancelled-month" label="Cancelados del mes" tone="red" icon={XCircle} value={k?.cancelled_this_month} />
      </div>
    </div>
  );
}

// ---------- Growth Chart ----------
const PERIODS = [
  { v: "week", label: "Semana" },
  { v: "month", label: "Mes" },
  { v: "quarter", label: "Trimestre" },
  { v: "year", label: "Año" },
];

export function GrowthChart() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState([]);
  useEffect(() => {
    api.get("/dashboard/growth", { params: { period } }).then(r => setData(r.data?.data || [])).catch(() => {});
  }, [period]);

  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Crecimiento</div>
          <div className="text-sm text-slate-700 mt-0.5">Clientes nuevos vs pérdidas en el tiempo</div>
        </div>
        <div className="flex items-center gap-0 border border-slate-200">
          {PERIODS.map(p => (
            <button
              key={p.v} onClick={() => setPeriod(p.v)} data-testid={`growth-${p.v}`}
              className={`px-3 py-1.5 text-xs font-medium border-r border-slate-200 last:border-r-0 transition-colors ${period === p.v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="g-new" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.45} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="g-cum" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area type="monotone" dataKey="cumulative" name="Total acumulado" stroke="#6366f1" strokeWidth={2} fill="url(#g-cum)" />
          <Area type="monotone" dataKey="new" name="Nuevos" stroke="#10b981" strokeWidth={2} fill="url(#g-new)" />
          <Area type="monotone" dataKey="lost" name="Perdidos" stroke="#94a3b8" strokeWidth={1.5} fillOpacity={0} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Client Pipeline (7 stages) ----------
const STAGES = [
  { key: "registered",         label: "Registrado",         color: "bg-slate-400",   text: "text-slate-700" },
  { key: "docs_completed",     label: "Docs completos",     color: "bg-blue-400",    text: "text-blue-700" },
  { key: "pending_assignment", label: "Pend. asignación",   color: "bg-indigo-400",  text: "text-indigo-700" },
  { key: "assigned",           label: "Asignado",           color: "bg-violet-500",  text: "text-violet-700" },
  { key: "connecting",         label: "Conectando",         color: "bg-amber-500",   text: "text-amber-700" },
  { key: "connected",          label: "Conectado",          color: "bg-teal-500",    text: "text-teal-700" },
  { key: "active",             label: "Activo",             color: "bg-emerald-500", text: "text-emerald-700" },
];

export function ClientPipeline() {
  const [data, setData] = useState([]);
  useSmartPoll(() => {
    api.get("/dashboard/client-pipeline").then(r => setData(r.data)).catch(() => {});
  }, 60000);
  const byStage = Object.fromEntries(data.map(d => [d.stage, d.count]));
  const max = Math.max(1, ...Object.values(byStage));

  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Pipeline de clientes</div>
          <div className="text-sm text-slate-700 mt-0.5">Estados desde registro hasta activación</div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold font-mono">Live</span>
      </div>
      <div className="space-y-2.5">
        {STAGES.map((s, idx) => {
          const count = byStage[s.key] || 0;
          const pct = (count / max) * 100;
          return (
            <div key={s.key} data-testid={`pipeline-${s.key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-400 tabular-nums w-4 text-right">{idx + 1}</span>
                  <span className="font-medium text-slate-700">{s.label}</span>
                </div>
                <span className="font-mono font-semibold text-slate-900 tabular-nums">{count}</span>
              </div>
              <div className="h-[6px] bg-slate-100 overflow-hidden">
                <div className={`h-full ${s.color} transition-all duration-700`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Payment Chart (financial only) ----------
export function PaymentChart() {
  const [data, setData] = useState([]);
  useEffect(() => {
    api.get("/dashboard/payment-chart", { params: { months: 6 } }).then(r => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="mb-4 pb-3 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Cobros · últimos 6 meses</div>
        <div className="text-sm text-slate-700 mt-0.5">Pagos a tiempo · en gracia · tardíos</div>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip contentStyle={{ border: "1px solid #e2e8f0", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="on_time" name="A tiempo" stackId="a" fill="#10b981" />
          <Bar dataKey="grace" name="En gracia" stackId="a" fill="#f59e0b" />
          <Bar dataKey="late" name="Tardíos" stackId="a" fill="#ef4444" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Invoice Summary (financial only) ----------
export function InvoiceSummary() {
  const [d, setD] = useState(null);
  useSmartPoll(() => {
    api.get("/dashboard/invoice-summary").then(r => setD(r.data)).catch(() => {});
  }, 60000);
  const bs = d?.by_status || {};
  const pt = d?.payment_timing || {};
  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="flex items-baseline justify-between mb-4 pb-3 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Facturas y pagos</div>
          <div className="text-sm text-slate-700 mt-0.5">Resumen Square</div>
        </div>
        <div className="text-2xl font-semibold font-mono tabular-nums text-slate-900">{d?.total_generated ?? 0}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <MiniStat testid="is-delivered" label="Entregadas" tone="emerald" value={d?.delivered} hint="emails enviados" />
        <MiniStat testid="is-pending-delivery" label="Pend. entrega" tone="amber" value={d?.pending_delivery} hint="sin email" />
        <MiniStat testid="is-paid" label="Pagadas" tone="emerald" value={bs.paid?.count} hint={`$${(bs.paid?.amount || 0).toFixed(2)}`} />
        <MiniStat testid="is-pending" label="Pendientes" tone="blue" value={bs.pending?.count} hint={`$${(bs.pending?.amount || 0).toFixed(2)}`} />
        <MiniStat testid="is-overdue" label="Vencidas" tone="red" value={bs.overdue?.count} hint={`$${(bs.overdue?.amount || 0).toFixed(2)}`} />
        <MiniStat testid="is-disc" label="Desconectadas" tone="red" value={bs.disconnected?.count} hint={`$${(bs.disconnected?.amount || 0).toFixed(2)}`} />
      </div>
      <div className="pt-4 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">Cumplimiento de pago</div>
        <div className="grid grid-cols-4 gap-3">
          <MiniStat testid="pt-on-time" label="A tiempo" tone="emerald" value={pt.on_time} />
          <MiniStat testid="pt-grace" label="En gracia" tone="amber" value={pt.grace} />
          <MiniStat testid="pt-late" label="Tardíos" tone="red" value={pt.late} />
          <MiniStat testid="pt-failed" label="Fallidos" tone="slate" value={pt.failed} />
        </div>
      </div>
    </div>
  );
}


// ---------- AgingBuckets ----------
export function AgingBuckets({ lang = "es" }) {
  const [data, setData] = useState(null);
  useSmartPoll(() => {
    api.get("/dashboard/aging").then((r) => setData(r.data)).catch(() => {});
  }, 60000);

  const BUCKETS_DEF = [
    { key: "current",  es: "Al día",      en: "Current",   tone: "bg-emerald-500", text: "text-emerald-700" },
    { key: "b1",       es: "1-30 días",   en: "1-30 days", tone: "bg-amber-400",   text: "text-amber-700" },
    { key: "b2",       es: "31-60 días",  en: "31-60 days",tone: "bg-orange-500",  text: "text-orange-700" },
    { key: "b3",       es: "61-90 días",  en: "61-90 days",tone: "bg-red-500",     text: "text-red-700" },
    { key: "b4",       es: "90+ días",    en: "90+ days",  tone: "bg-red-700",     text: "text-red-800" },
  ];

  const total = data?.total_amount || 0;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid="widget-aging">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">{lang === "en" ? "Aging" : "Antigüedad"}</div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white mt-0.5">{lang === "en" ? "Receivables aging" : "Antigüedad de saldos"}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{lang === "en" ? "Total" : "Total"}</div>
          <div className="font-mono font-semibold text-slate-900 dark:text-white">${total.toFixed(2)}</div>
        </div>
      </div>

      {/* Stacked bar visualization */}
      <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 flex overflow-hidden mb-4" data-testid="aging-bar">
        {BUCKETS_DEF.map((b) => {
          const bucket = data?.buckets?.[b.key];
          const pct = bucket?.pct || 0;
          return pct > 0 && (
            <div
              key={b.key}
              className={`h-full ${b.tone} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${b.es}: ${pct}%`}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-5 gap-2">
        {BUCKETS_DEF.map((b) => {
          const bucket = data?.buckets?.[b.key] || { count: 0, amount: 0, pct: 0 };
          return (
            <div key={b.key} className="text-center" data-testid={`aging-${b.key}`}>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${b.text} dark:opacity-80`}>{lang === "en" ? b.en : b.es}</div>
              <div className="font-mono text-sm font-semibold text-slate-900 dark:text-white mt-1">${bucket.amount.toFixed(0)}</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400">{bucket.count} {lang === "en" ? "inv." : "facts."} · {bucket.pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------- TopDebtors ----------
import { Link } from "react-router-dom";
import { ArrowRight, Warning as WarningIcon, EnvelopeSimple, PlugsConnected, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function TopDebtors({ lang = "es" }) {
  const [data, setData] = useState([]);
  useSmartPoll(() => {
    api.get("/dashboard/top-debtors", { params: { limit: 5 } })
      .then((r) => setData(r.data)).catch(() => setData([]));
  }, 60000);

  const sendReminder = async (clientId, name) => {
    try {
      await api.post(`/clients/${clientId}/remind-overdue`).catch(() => {});
      toast.success(lang === "en" ? `Reminder sent to ${name}` : `Recordatorio enviado a ${name}`);
    } catch {
      toast.error(lang === "en" ? "Could not send reminder" : "No se pudo enviar el recordatorio");
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid="widget-top-debtors">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">{lang === "en" ? "Receivables" : "Cobranza"}</div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white mt-0.5">{lang === "en" ? "Top 5 debtors" : "Top 5 deudores"}</div>
        </div>
        <Link to="/clientes" className="text-[11px] text-slate-500 hover:text-slate-900 dark:hover:text-white inline-flex items-center gap-1">
          {lang === "en" ? "View clients" : "Ver clientes"} <ArrowRight size={11} />
        </Link>
      </div>
      {data.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-400">{lang === "en" ? "No outstanding balances" : "Sin saldos pendientes"}</div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.map((d, i) => (
            <div key={d.client_id || i} className="py-2.5 flex items-center justify-between gap-3 group hover:bg-slate-50 dark:hover:bg-slate-800/40 -mx-2 px-2 rounded transition-colors" data-testid={`debtor-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-400 w-3">{i + 1}</span>
                  <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{d.client_name}</div>
                </div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 ml-5 mt-0.5">
                  {d.invoice_count} {lang === "en" ? "inv." : "facts."}
                  {d.max_days_overdue > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-semibold">
                      <WarningIcon size={9} weight="fill" /> {d.max_days_overdue}d
                    </span>
                  )}
                </div>
              </div>
              {/* Hover-revealed quick actions */}
              <div className="hidden group-hover:flex items-center gap-0.5" data-testid={`debtor-actions-${i}`}>
                <button
                  onClick={() => sendReminder(d.client_id, d.client_name)}
                  className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded transition-colors"
                  title={lang === "en" ? "Send reminder" : "Enviar recordatorio"}
                  data-testid={`debtor-remind-${i}`}
                >
                  <EnvelopeSimple size={13} />
                </button>
                <Link
                  to={`/clientes`}
                  className="p-1.5 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                  title={lang === "en" ? "View history" : "Ver historial"}
                  data-testid={`debtor-view-${i}`}
                >
                  <UserCircle size={13} />
                </Link>
              </div>
              <div className="font-mono text-sm font-semibold text-slate-900 dark:text-white shrink-0 group-hover:hidden">
                ${d.total_outstanding.toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============ PHASE 1 — BUSINESS METRICS WIDGETS ============
import { fmtMoney } from "@/components/Shared";
import { TrendUp, TrendDown, Wallet, Target, Funnel as FunnelIcon, ChartLineUp } from "@phosphor-icons/react";

// ---------- MRR Widget ----------
export function MrrWidget({ lang = "es" }) {
  const [d, setD] = useState(null);
  useSmartPoll(() => {
    api.get("/dashboard/mrr").then((r) => setD(r.data)).catch(() => {});
  }, 90000);

  const limited = !!d?.limited;
  const mask = (v) => (limited ? "$•••" : v);
  const delta = d?.delta_pct;
  const positive = delta !== null && delta > 0;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid="widget-mrr">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">MRR</div>
          <div className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{lang === "en" ? "Monthly recurring revenue" : "Ingreso recurrente mensual"}</div>
        </div>
        <Wallet size={16} className="text-slate-400" />
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <div className="text-3xl font-semibold font-mono tabular-nums text-slate-900 dark:text-white">
          {d ? mask(fmtMoney(d.mrr)) : "—"}
        </div>
        {delta !== null && delta !== undefined && isFinite(delta) && Math.abs(delta) >= 0.1 && (
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono font-semibold ${positive ? "text-emerald-600" : "text-rose-600"}`}>
            {positive ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
            {positive ? "+" : ""}{delta.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mb-4">
        ARR: <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{d ? mask(fmtMoney(d.arr)) : "—"}</span>
        <span className="mx-2 text-slate-300">·</span>
        ARPU: <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{d ? mask(fmtMoney(d.arpu)) : "—"}</span>
        <span className="mx-2 text-slate-300">·</span>
        <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{d?.active_clients ?? 0}</span> {lang === "en" ? "active" : "activos"}
      </div>

      {/* 6-month sparkline */}
      {d?.history?.length > 0 && (
        <div className="h-14 -mx-1 mb-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.history} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
              <defs>
                <linearGradient id="mrr-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F172A" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip
                contentStyle={{ background: "#0F172A", border: "1px solid #1e293b", borderRadius: 6, fontSize: 11, padding: "4px 8px" }}
                labelStyle={{ color: "#94a3b8", fontSize: 10 }}
                itemStyle={{ color: "#fff", padding: 0 }}
                formatter={(v) => [limited ? "$•••" : fmtMoney(v), "MRR"]}
              />
              <Area type="monotone" dataKey="mrr" stroke="#0F172A" strokeWidth={1.75} fill="url(#mrr-grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top 3 plans breakdown */}
      {d?.by_plan?.length > 0 && !limited && (
        <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
          {d.by_plan.slice(0, 3).map((p) => {
            const pct = d.mrr > 0 ? (p.amount / d.mrr) * 100 : 0;
            return (
              <div key={p.plan_id || "no-plan"} className="flex items-center gap-2 text-[11px]" data-testid={`mrr-plan-${p.plan_id || "none"}`}>
                <div className="w-24 truncate text-slate-600 dark:text-slate-400">{p.plan_name}</div>
                <div className="flex-1 h-1 bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div className="h-full bg-slate-900 dark:bg-white" style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <div className="w-16 text-right font-mono tabular-nums text-slate-700 dark:text-slate-300">{fmtMoney(p.amount)}</div>
                <div className="w-8 text-right font-mono text-slate-400 text-[10px]">{p.count}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Churn Widget ----------
export function ChurnWidget({ lang = "es" }) {
  const [d, setD] = useState(null);
  useSmartPoll(() => {
    api.get("/dashboard/churn").then((r) => setD(r.data)).catch(() => {});
  }, 90000);

  const rate = d?.rate ?? 0;
  const retention = d?.retention ?? 100;
  const deltaPP = d?.delta_pp;
  const tone = rate < 3 ? "emerald" : rate < 7 ? "amber" : "red";
  const toneCls = {
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    red: "text-rose-600",
  }[tone];

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid="widget-churn">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">CHURN</div>
          <div className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{lang === "en" ? "Monthly cancellation rate" : "Tasa de cancelación mensual"}</div>
        </div>
        <Target size={16} className="text-slate-400" />
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <div className={`text-3xl font-semibold font-mono tabular-nums ${toneCls}`}>
          {d ? `${rate.toFixed(2)}%` : "—"}
        </div>
        {deltaPP !== null && deltaPP !== undefined && Math.abs(deltaPP) >= 0.1 && (
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono font-semibold ${deltaPP > 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {deltaPP > 0 ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
            {deltaPP > 0 ? "+" : ""}{deltaPP.toFixed(2)}pp
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mb-4">
        {lang === "en" ? "Retention" : "Retención"}: <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{retention.toFixed(2)}%</span>
        <span className="mx-2 text-slate-300">·</span>
        <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{d?.current_lost ?? 0}</span>/{d?.current_active_at_start ?? 0} {lang === "en" ? "lost this month" : "perdidos este mes"}
      </div>

      {/* 6-month sparkline */}
      {d?.history?.length > 0 && (
        <div className="h-14 -mx-1 mb-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.history} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
              <defs>
                <linearGradient id="churn-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip
                contentStyle={{ background: "#0F172A", border: "1px solid #1e293b", borderRadius: 6, fontSize: 11, padding: "4px 8px" }}
                labelStyle={{ color: "#94a3b8", fontSize: 10 }}
                itemStyle={{ color: "#fff", padding: 0 }}
                formatter={(v) => [`${v.toFixed(2)}%`, "Churn"]}
              />
              <Area type="monotone" dataKey="rate" stroke="#ef4444" strokeWidth={1.75} fill="url(#churn-grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-6 gap-1 pt-2 border-t border-slate-100 dark:border-slate-800">
        {(d?.history || []).map((h, i) => (
          <div key={i} className="text-center" data-testid={`churn-hist-${i}`}>
            <div className="text-[9px] text-slate-400 font-mono">{h.label}</div>
            <div className="text-[10px] font-mono text-slate-700 dark:text-slate-300 mt-0.5">{h.rate.toFixed(1)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Funnel Widget (admin only — auto-hides on 403) ----------
export function FunnelWidget({ lang = "es" }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useSmartPoll(() => {
    api.get("/dashboard/funnel").then((r) => setD(r.data)).catch(() => setErr(true));
  }, 90000);

  if (err) return null;

  const maxCount = Math.max(1, ...(d?.stages?.map((s) => s.count) || [1]));

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid="widget-funnel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{lang === "en" ? "FUNNEL" : "EMBUDO"}</div>
          <div className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{lang === "en" ? "Conversion funnel · global" : "Embudo de conversión · global"}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{lang === "en" ? "Overall" : "Total"}</div>
          <div className="font-mono font-semibold text-slate-900 dark:text-white">{d?.overall_rate ?? 0}%</div>
        </div>
      </div>

      <div className="space-y-2.5">
        {(d?.stages || []).map((s, idx) => {
          const width = (s.count / maxCount) * 100;
          const prevCount = idx > 0 ? d.stages[idx - 1].count : s.count;
          const dropoff = idx > 0 ? prevCount - s.count : 0;
          return (
            <div key={s.key} data-testid={`funnel-${s.key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-400 tabular-nums w-4 text-right">{idx + 1}</span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">{s.label}</span>
                  {idx > 0 && (
                    <span className="text-[10px] font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                      {s.rate_from_prev}%
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {dropoff > 0 && (
                    <span className="text-[10px] font-mono text-rose-600">−{dropoff}</span>
                  )}
                  <span className="font-mono font-semibold text-slate-900 dark:text-white tabular-nums">{s.count}</span>
                </div>
              </div>
              <div className="h-2 bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div
                  className={`h-full transition-all duration-700 ${
                    idx === 0 ? "bg-slate-400"
                    : idx === 1 ? "bg-indigo-500"
                    : idx === 2 ? "bg-teal-500"
                    : "bg-emerald-500"
                  }`}
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Forecast Widget ----------
export function ForecastWidget({ lang = "es" }) {
  const [d, setD] = useState(null);
  useSmartPoll(() => {
    api.get("/dashboard/forecast").then((r) => setD(r.data)).catch(() => {});
  }, 90000);

  const limited = !!d?.limited;
  const mask = (v) => (limited ? "$•••" : v);
  const forecast = d?.forecast ?? 0;
  const mtd = d?.mtd ?? 0;
  const delta = d?.delta_pct;
  const positive = delta !== null && delta > 0;
  const confidencePct = Math.round((d?.confidence ?? 0) * 100);
  const progress = d ? Math.round((d.days_elapsed / d.days_in_month) * 100) : 0;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid="widget-forecast">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">FORECAST</div>
          <div className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{lang === "en" ? "End-of-month projection" : "Proyección fin de mes"}</div>
        </div>
        <ChartLineUp size={16} className="text-slate-400" />
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <div className="text-3xl font-semibold font-mono tabular-nums text-slate-900 dark:text-white">
          {d ? mask(fmtMoney(forecast)) : "—"}
        </div>
        {delta !== null && delta !== undefined && isFinite(delta) && Math.abs(delta) >= 0.1 && (
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono font-semibold ${positive ? "text-emerald-600" : "text-rose-600"}`}>
            {positive ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
            {positive ? "+" : ""}{delta.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        {lang === "en" ? "vs" : "vs"} <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{d ? mask(fmtMoney(d.prev_month_total)) : "—"}</span> {lang === "en" ? "last month" : "mes anterior"}
      </div>

      {/* Progress bar: days elapsed / total */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-slate-500">{lang === "en" ? "Month progress" : "Progreso del mes"}</span>
          <span className="font-mono text-slate-700 dark:text-slate-300">{d?.days_elapsed ?? 0}/{d?.days_in_month ?? 0} {lang === "en" ? "days" : "días"}</span>
        </div>
        <div className="h-1.5 bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div className="h-full bg-amber-500 transition-all duration-700" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* MTD vs Forecast bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-slate-500">{lang === "en" ? "MTD collected" : "Cobrado MTD"}</span>
          <span className="font-mono text-slate-700 dark:text-slate-300">
            {d ? mask(fmtMoney(mtd)) : "—"} · {forecast > 0 ? Math.round((mtd / forecast) * 100) : 0}%
          </span>
        </div>
        <div className="h-1.5 bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${forecast > 0 ? Math.min(100, (mtd / forecast) * 100) : 0}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 text-[11px]">
        <div>
          <div className="text-slate-500">Run-rate</div>
          <div className="font-mono font-semibold text-slate-900 dark:text-white">{d ? mask(fmtMoney(d.run_rate)) : "—"}</div>
        </div>
        <div>
          <div className="text-slate-500">Pipeline</div>
          <div className="font-mono font-semibold text-slate-900 dark:text-white">{d ? mask(fmtMoney(d.pipeline_forecast)) : "—"}</div>
        </div>
        <div>
          <div className="text-slate-500">{lang === "en" ? "Pending" : "Pendiente"}</div>
          <div className="font-mono font-semibold text-slate-900 dark:text-white">
            {d ? mask(fmtMoney(d.pending_amount)) : "—"} <span className="text-slate-400 font-normal">({d?.pending_count ?? 0})</span>
          </div>
        </div>
        <div>
          <div className="text-slate-500">{lang === "en" ? "Confidence" : "Confianza"}</div>
          <div className="font-mono font-semibold text-slate-900 dark:text-white">{confidencePct}%</div>
        </div>
      </div>
    </div>
  );
}


// ============ PHASE 2 — TEAM & AGENTS WIDGETS ============

import { Users as UsersIcon, Medal, Trophy } from "@phosphor-icons/react";

// Deterministic initials avatar (color derived from email hash)
function AgentAvatar({ name, email, size = 32 }) {
  const initials = (name || email || "?")
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";
  const hash = Array.from(email || name || "").reduce((a, c) => a + c.charCodeAt(0), 0);
  const palettes = [
    "bg-slate-800 text-slate-100",
    "bg-indigo-900 text-indigo-100",
    "bg-sky-900 text-sky-100",
    "bg-teal-900 text-teal-100",
    "bg-emerald-900 text-emerald-100",
    "bg-blue-900 text-blue-100",
    "bg-cyan-900 text-cyan-100",
  ];
  const cls = palettes[hash % palettes.length];
  return (
    <div
      className={`shrink-0 flex items-center justify-center font-semibold text-[11px] tracking-wide rounded-full ${cls}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

const RANK_BADGE = [Trophy, Medal, Medal];
const RANK_TONE = ["text-amber-500", "text-slate-400", "text-orange-700"];

export function AgentsRankingWidget({ lang = "es" }) {
  const [data, setData] = useState(null);
  useSmartPoll(() => {
    api.get("/dashboard/top-agents", { params: { limit: 8 } })
      .then((r) => setData(r.data)).catch(() => setData({ agents: [], totals: {} }));
  }, 90000);

  const agents = data?.agents || [];
  const totals = data?.totals || {};
  const limited = !!data?.limited;
  const maxConv = Math.max(1, ...agents.map((a) => a.conversions_30d || 0));

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid="widget-top-agents">
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{lang === "en" ? "TEAM" : "EQUIPO"}</div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white mt-0.5">
            {lang === "en" ? "Agents leaderboard · last 30 days" : "Ranking de agentes · últimos 30 días"}
          </div>
        </div>
        <UsersIcon size={16} className="text-slate-400" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2.5" data-testid="agents-total-agents">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{lang === "en" ? "Agents" : "Agentes"}</div>
          <div className="mt-1 text-lg font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{totals.total_agents ?? 0}</div>
        </div>
        <div className="rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2.5" data-testid="agents-total-conversions">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{lang === "en" ? "Conversions" : "Conversiones"}</div>
          <div className="mt-1 text-lg font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{totals.total_conversions_30d ?? 0}</div>
        </div>
        <div className="rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2.5" data-testid="agents-total-pipeline">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Pipeline</div>
          <div className="mt-1 text-lg font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{totals.total_pipeline ?? 0}</div>
        </div>
        <div className="rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2.5" data-testid="agents-total-mrr">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">MRR</div>
          <div className="mt-1 text-lg font-semibold font-mono tabular-nums text-slate-900 dark:text-white">
            {limited ? "$•••" : fmtMoney(totals.total_mrr || 0)}
          </div>
        </div>
      </div>

      {agents.length === 0 ? (
        <div className="py-10 text-center text-xs text-slate-400">
          {lang === "en" ? "No agent activity yet" : "Aún no hay actividad de agentes"}
        </div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {agents.map((a, i) => {
            const RankIcon = i < 3 ? RANK_BADGE[i] : null;
            const rankTone = i < 3 ? RANK_TONE[i] : "text-slate-300";
            const pct = ((a.conversions_30d || 0) / maxConv) * 100;
            return (
              <div key={a.email} className="py-3 flex items-center gap-3" data-testid={`agent-row-${i}`}>
                <div className="w-6 flex items-center justify-center shrink-0">
                  {RankIcon
                    ? <RankIcon size={14} weight="fill" className={rankTone} />
                    : <span className="text-[10px] font-mono text-slate-400 tabular-nums">{i + 1}</span>}
                </div>
                <AgentAvatar name={a.name} email={a.email} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{a.name}</div>
                    {a.is_sub_agent && (
                      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700 shrink-0">SUB</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-slate-100 dark:bg-slate-800 overflow-hidden rounded-sm">
                      <div className="h-full bg-slate-900 dark:bg-white transition-all duration-700" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-mono tabular-nums text-slate-500 w-8 text-right">{a.conv_rate}%</span>
                  </div>
                </div>
                <div className="hidden md:grid grid-cols-4 gap-3 text-right shrink-0">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Conv</div>
                    <div className="text-sm font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{a.conversions_30d}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{lang === "en" ? "Active" : "Activos"}</div>
                    <div className="text-sm font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{a.active_clients}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Pipe</div>
                    <div className="text-sm font-semibold font-mono tabular-nums text-slate-700 dark:text-slate-300">{a.pipeline}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">MRR</div>
                    <div className="text-sm font-semibold font-mono tabular-nums text-slate-900 dark:text-white">
                      {limited ? "$•••" : fmtMoney(a.mrr || 0)}
                    </div>
                  </div>
                </div>
                {/* Mobile compact metrics */}
                <div className="md:hidden text-right shrink-0">
                  <div className="text-sm font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{a.conversions_30d}</div>
                  <div className="text-[10px] text-slate-400 font-mono">conv</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
